import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import apiWithAuth from '../../services/apiWithAuth';
import { useAuth } from '../../contexts/AuthContext';
import { 
  fetchShopProducts, 
  createProduct, 
  updateProduct, 
  deleteProduct, 
  approveProduct, 
  fetchCategoryTree,
  uploadMultipleFiles,
  uploadSingleFile,
  bulkAddProducts,
} from '../../services/api';
import CategoryTreeSelect from '../common/CategoryTreeSelect';
import './ShopManagement.css';
import '../common/CategoryTreeSelect.css';

/** Backend limit for product image/video uploads per product (upload/multiple). */
const MAX_PRODUCT_MEDIA_UPLOADS = 10;

const ShopProducts = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Video modal state
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [currentProductVideos, setCurrentProductVideos] = useState([]);
  
  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentProductImages, setCurrentProductImages] = useState([]);
  
  // Carousel state for images and videos in table
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  const [currentVideoIndices, setCurrentVideoIndices] = useState({});
  
  // Product management state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [productSearch, setProductSearch] = useState("");
  const [approved, setApproved] = useState(undefined);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sort, setSort] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    mrp: "",
    hsnCode: "",
    category: "",
    tags: "",
    image: [],
    videos: [],
    sponsored: false,
  });

  // Image upload state
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(false);

  // Video upload state
  const [uploadingVideos, setUploadingVideos] = useState(false);

  // Bulk add products (POST /admin-panel/products/bulk-add-products)
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkSamePriceForAll, setBulkSamePriceForAll] = useState(false);
  const [bulkGlobalPrice, setBulkGlobalPrice] = useState('');
  const [bulkSameMrpForAll, setBulkSameMrpForAll] = useState(false);
  const [bulkGlobalMrp, setBulkGlobalMrp] = useState('');
  const [bulkSameHsnForAll, setBulkSameHsnForAll] = useState(false);
  const [bulkGlobalHsn, setBulkGlobalHsn] = useState('');
  const [bulkSameCategoryForAll, setBulkSameCategoryForAll] = useState(false);
  const [bulkGlobalCategoryId, setBulkGlobalCategoryId] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  /** Which bulk row is uploading, and image vs video (so only that dropzone shows "Uploading..."). */
  const [bulkRowUploading, setBulkRowUploading] = useState(null);

  const newBulkRow = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name: '',
    description: '',
    categoryId: '',
    price: '',
    mrp: '',
    hsnCode: '',
    image: [],
    videos: [],
  });

  const resetBulkForm = () => {
    setBulkRows([newBulkRow()]);
    setBulkSamePriceForAll(false);
    setBulkGlobalPrice('');
    setBulkSameMrpForAll(false);
    setBulkGlobalMrp('');
    setBulkSameHsnForAll(false);
    setBulkGlobalHsn('');
    setBulkSameCategoryForAll(false);
    setBulkGlobalCategoryId('');
    setBulkError(null);
    setBulkRowUploading(null);
  };

  const openBulkModal = () => {
    setIsCreating(false);
    resetForm();
    resetBulkForm();
    setBulkModalOpen(true);
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkError(null);
    setBulkRowUploading(null);
  };

  const extractUploadedUrls = (response) => {
    const data = response?.data;
    if (!data) return [];
    const inner = data.data !== undefined ? data.data : data;
    if (Array.isArray(inner.file_urls)) return inner.file_urls;
    if (inner.file_url) return [inner.file_url];
    return [];
  };

  const canSubmitBulk = useMemo(() => {
    if (!bulkRows.length) return false;
    const priceGlobalOk =
      !bulkSamePriceForAll ||
      (String(bulkGlobalPrice).trim() !== '' &&
        Number.isFinite(Number(bulkGlobalPrice)));
    if (!priceGlobalOk) return false;
    const categoryGlobalOk =
      !bulkSameCategoryForAll || !!String(bulkGlobalCategoryId || '').trim();
    if (!categoryGlobalOk) return false;
    return bulkRows.every((row) => {
      const nameOk = String(row.name || '').trim() !== '';
      const catOk =
        bulkSameCategoryForAll || !!String(row.categoryId || '').trim();
      const imgOk = Array.isArray(row.image) && row.image.length > 0;
      const priceOk =
        bulkSamePriceForAll ||
        (String(row.price).trim() !== '' &&
          Number.isFinite(Number(row.price)));
      return nameOk && catOk && imgOk && priceOk;
    });
  }, [
    bulkRows,
    bulkSamePriceForAll,
    bulkGlobalPrice,
    bulkSameCategoryForAll,
    bulkGlobalCategoryId,
  ]);

  /** When "same category for all" is on, keep every row's categoryId in sync for validation and display. */
  useEffect(() => {
    if (!bulkModalOpen || !bulkSameCategoryForAll || !bulkGlobalCategoryId) return;
    setBulkRows((prev) =>
      prev.map((r) => ({ ...r, categoryId: bulkGlobalCategoryId }))
    );
  }, [bulkModalOpen, bulkSameCategoryForAll, bulkGlobalCategoryId]);

  const canSubmit = useMemo(() => {
    if (!formData.name || !formData.price || !formData.category) return false;
    return true;
  }, [formData]);

  // Handle video click to open modal
  const handleVideoClick = (videoUrl, videos, index) => {
    setSelectedVideo(videoUrl);
    setCurrentProductVideos(videos);
    setSelectedVideoIndex(index);
    setVideoModalOpen(true);
  };

  // Navigate videos in modal
  const navigateVideo = (direction) => {
    const newIndex = selectedVideoIndex + direction;
    if (newIndex >= 0 && newIndex < currentProductVideos.length) {
      setSelectedVideoIndex(newIndex);
      setSelectedVideo(currentProductVideos[newIndex]);
    }
  };

  // Handle image click to open modal
  const handleImageClick = (imageUrl, images, index) => {
    setSelectedImage(imageUrl);
    setCurrentProductImages(images);
    setSelectedImageIndex(index);
    setImageModalOpen(true);
  };

  // Navigate images in modal
  const navigateImage = (direction) => {
    const newIndex = selectedImageIndex + direction;
    if (newIndex >= 0 && newIndex < currentProductImages.length) {
      setSelectedImageIndex(newIndex);
      setSelectedImage(currentProductImages[newIndex]);
    }
  };

  // Navigate carousel images in table
  const navigateTableImage = (productId, images, direction) => {
    const currentIndex = currentImageIndices[productId] || 0;
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < images.length) {
      setCurrentImageIndices(prev => ({ ...prev, [productId]: newIndex }));
    }
  };

  // Navigate carousel videos in table
  const navigateTableVideo = (productId, videos, direction) => {
    const currentIndex = currentVideoIndices[productId] || 0;
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < videos.length) {
      setCurrentVideoIndices(prev => ({ ...prev, [productId]: newIndex }));
    }
  };

  // Fetch shop details to get complete owner information
  const fetchShopDetails = async (shopId) => {
    try {
      const response = await apiWithAuth.get(`/admin-panel/shops/${shopId}`);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching shop details:', err);
      return null;
    }
  };
  const fetchShops = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiWithAuth.get('/admin-panel/shops');
      if (response.data.success) {
        setShops(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch shops');
      }
    } catch (err) {
      console.error('Error fetching shops:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch shops');
    } finally {
      setLoading(false);
    }
  };

  // Load products for selected shop
  const loadProducts = async () => {
    if (!selectedShop) return;
    
    // Try multiple sources for userId: owner._id, ownerId, userId, createdBy, or current user
    let userId = selectedShop.owner?._id || selectedShop.ownerId || selectedShop.userId || selectedShop.createdBy || user?._id;
    
    // Validate that we have shopId
    if (!selectedShop._id) {
      setError('Shop ID is missing');
      return;
    }
    
    // If userId is missing, try to fetch shop details to get complete owner information
    if (!userId) {
      console.warn('User ID not found in shop object, attempting to fetch shop details:', selectedShop);
      const shopDetails = await fetchShopDetails(selectedShop._id);
      if (shopDetails) {
        userId = shopDetails.owner?._id || shopDetails.ownerId || shopDetails.userId || shopDetails.createdBy;
        // Update selectedShop with the fetched data
        setSelectedShop(prev => prev ? { ...prev, ...shopDetails } : null);
      }
    }
    
    // If still no userId, use current user as fallback but don't block the request
    if (!userId) {
      userId = user?._id || 'unknown';
      console.warn('Using fallback userId:', userId);
    }
    
    try {
      setLoading(true);
      setError(null);
      const res = await fetchShopProducts(selectedShop._id, {
        userId,
        page,
        limit,
        search: productSearch || undefined,
        approved,
        includeInactive: includeInactive ? "true" : undefined,
        sort,
        order,
      });
      const list = res?.data?.data || [];
      setProducts(list);
      const p = res?.data?.pagination;
      setTotalPages(p?.totalPages || 1);
    } catch (e) {
      setError("Failed to load products");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const res = await fetchCategoryTree({ page: 1, limit: 1000 });
      const tree = res?.data?.data || [];
      setCategories(tree);
    } catch (e) {
      console.error('Failed to load categories', e);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchShops();
    loadCategories();
  }, []);

  // Auto-select shop if coming from shop details page
  useEffect(() => {
    const selectShopWithDetails = async () => {
      if (location.state?.selectedShopId && shops.length > 0) {
        const shop = shops.find(s => s._id === location.state.selectedShopId);
        if (shop) {
          // Fetch full shop details to get owner information FIRST
          const fullShopDetails = await fetchShopDetails(shop._id);
          if (fullShopDetails) {
            setSelectedShop(fullShopDetails);
            // Store shopId in localStorage for API headers
            localStorage.setItem('current_shop_id', fullShopDetails._id);
          } else {
            setSelectedShop(shop);
            localStorage.setItem('current_shop_id', shop._id);
          }
        }
      }
    };
    selectShopWithDetails();
  }, [location.state, shops]);

  // Only load products when selectedShop is fully loaded with owner details
  useEffect(() => {
    if (selectedShop && selectedShop._id) {
      loadProducts();
    }
  }, [selectedShop?._id, selectedShop?.owner?._id, selectedShop?.ownerId, page, limit, productSearch, approved, includeInactive, sort, order]);

  // Filter shops based on search term and status
  const filteredShops = shops.filter(shop => {
    const matchesSearch = shop.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shop.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || shop.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleShopSelect = async (shop) => {
    setSelectedShop(shop);
    setProducts([]);
    setPage(1);
    setProductSearch("");
    setApproved(undefined);
    setIncludeInactive(false);
    setIsCreating(false);
    setBulkModalOpen(false);
    resetForm();
    
    // Store shopId in localStorage for API headers
    if (shop && shop._id) {
      localStorage.setItem('current_shop_id', shop._id);
    }
    
    // Fetch complete shop details to ensure we have owner information
    const fullShopDetails = await fetchShopDetails(shop._id);
    if (fullShopDetails) {
      setSelectedShop(fullShopDetails);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'APPROVED': return 'status-badge status-approved';
      case 'ACTIVE': return 'status-badge status-approved';
      case 'PENDING': return 'status-badge status-pending';
      case 'REJECTED': return 'status-badge status-rejected';
      case 'INACTIVE': return 'status-badge status-rejected';
      case 'HOLD': return 'status-badge status-hold';
      default: return 'status-badge status-default';
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      price: "",
      mrp: "",
      hsnCode: "",
      category: "",
      tags: "",
      image: [],
      videos: [],
      sponsored: false,
    });
    setEditingId(null);
    setUploadingImages(false);
    setUploadingVideos(false);
  };

  const onChangeField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const populateFormForEdit = (p) => {
    setIsCreating(true);
    setEditingId(p._id);
    setFormData({
      name: p.name || "",
      description: p.description || "",
      price: p.price ?? "",
      mrp: p.mrp != null && p.mrp !== "" ? String(p.mrp) : "",
      hsnCode:
        p.hsnCode != null && p.hsnCode !== ""
          ? String(p.hsnCode)
          : p.hsn != null && p.hsn !== ""
            ? String(p.hsn)
            : "",
      category: p.category?._id || p.category || "",
      tags: Array.isArray(p.tags) ? p.tags.join(", ") : "",
      image: Array.isArray(p.image) ? p.image : [],
      videos: Array.isArray(p.videos) ? p.videos : [],
      sponsored: !!p.sponsored,
    });
  };

  const buildBody = () => {
    // Forcefully get userId from multiple sources
    let userId = selectedShop.owner?._id || selectedShop.ownerId || selectedShop.userId || selectedShop.createdBy || user?._id;
    
    // If userId is still not available, use a fallback (to prevent undefined)
    if (!userId) {
      userId = user?._id || 'admin-fallback';
      console.warn('Using fallback userId for product creation:', userId);
    }
    
    const tags = (formData.tags || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const body = {
      shopId: selectedShop._id,
      userId: userId, // Forcefully include userId
      name: formData.name.trim(),
      description: formData.description?.trim(),
      price: Number(formData.price),
      image: formData.image,
      videos: formData.videos,
      category: formData.category,
      tags,
      sponsored: !!formData.sponsored,
      addBy: "admin",
    };
    const mrpStr = String(formData.mrp ?? "").trim();
    if (mrpStr !== "") {
      const mrpNum = Number(mrpStr);
      if (Number.isFinite(mrpNum)) body.mrp = mrpNum;
    }
    const hsnStr = String(formData.hsnCode ?? "").trim();
    if (hsnStr !== "") body.hsnCode = hsnStr;
    return body;
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    if (uploadingImages || uploadingVideos) return;
    try {
      setLoading(true);
      setError(null);
      const body = buildBody();
      await createProduct(body);
      resetForm();
      setIsCreating(false);
      await loadProducts();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to create product";
      setError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    if (uploadingImages || uploadingVideos) return;
    try {
      setLoading(true);
      setError(null);
      const body = buildBody();
      await updateProduct(editingId, body);
      resetForm();
      setIsCreating(false);
      await loadProducts();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update product";
      setError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const toggleApproval = async (p) => {
    try {
      setLoading(true);
      setError(null);
      await approveProduct(p._id, {
        isApproved: !p.isApproved,
        isRejected: false,
      });
      await loadProducts();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to update approval";
      setError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      setLoading(true);
      setError(null);
      await deleteProduct(id, { hard: false });
      await loadProducts();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to delete product";
      setError(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Handle image upload using the multiple file upload API
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Get current images from state
    const currentImages = formData.image || [];
    const currentCount = currentImages.length;
    const availableSlots = MAX_PRODUCT_MEDIA_UPLOADS - currentCount;
    if (availableSlots <= 0) {
      setError(`Maximum ${MAX_PRODUCT_MEDIA_UPLOADS} images allowed. Please remove some images first.`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, availableSlots);
    
    // Validate that all files are proper File objects
    const validFiles = filesToUpload.filter(file => {
      const isValid = file instanceof File || file instanceof Blob;
      if (!isValid) {
        console.warn('Skipping invalid file:', file);
      }
      return isValid;
    });
    
    if (validFiles.length === 0) {
      setError("No valid files selected for upload.");
      return;
    }
    
    if (validFiles.length < filesToUpload.length) {
      console.warn(`Filtered out ${filesToUpload.length - validFiles.length} invalid files`);
    }
    
    setUploadingImages(true);
    setError(null);

    try {
      // Get shopId and userId from selectedShop
      let shopId = selectedShop?._id;
      let userId = selectedShop?.owner?._id || selectedShop?.ownerId || selectedShop?.userId || selectedShop?.createdBy || user?._id;
      
      // If shopId or userId is missing, fetch full shop details first
      if ((!shopId || !userId) && selectedShop?._id) {
        const fullDetails = await fetchShopDetails(selectedShop._id);
        if (fullDetails) {
          setSelectedShop(fullDetails);
          shopId = fullDetails._id;
          userId = fullDetails.owner?._id || fullDetails.ownerId || fullDetails.userId || fullDetails.createdBy;
        }
      }
      
      // Validate we have the required IDs
      if (!shopId) {
        throw new Error('Shop ID is missing. Please select a shop first.');
      }
      if (!userId) {
        throw new Error('User ID is missing. Please ensure the shop has an owner.');
      }
      
      console.log('Uploading images:', {
        fileCount: validFiles.length,
        shopId,
        userId,
        type: 'product'
      });

      const response = await uploadMultipleFiles(validFiles, shopId, userId, 'product');

      // Handle both singular and plural response formats
      const newImageUrls = response.data.file_urls || (response.data.file_url ? [response.data.file_url] : []);
      if (newImageUrls.length > 0) {
        // Use functional update to get the latest state
        setFormData(prev => ({
          ...prev,
          image: [...(prev.image || []), ...newImageUrls]
        }));
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      const msg = err?.response?.data?.message || err.message || "Failed to upload images";
      setError(msg);
    } finally {
      setUploadingImages(false);
      // Reset the input
      e.target.value = '';
    }
  };

  // Remove image from the list
  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      image: prev.image.filter((_, i) => i !== index)
    }));
  };

  // Handle video upload using the multiple file upload API
  const handleVideoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Get current videos from state
    const currentVideos = formData.videos || [];
    const currentCount = currentVideos.length;
    const availableSlots = MAX_PRODUCT_MEDIA_UPLOADS - currentCount;
    if (availableSlots <= 0) {
      setError(`Maximum ${MAX_PRODUCT_MEDIA_UPLOADS} videos allowed. Please remove some videos first.`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, availableSlots);
    
    // Validate that all files are proper File objects and are videos
    const validFiles = filesToUpload.filter(file => {
      const isValid = file instanceof File || file instanceof Blob;
      const isVideo = file.type && file.type.startsWith('video/');
      if (!isValid || !isVideo) {
        console.warn('Skipping invalid file:', file);
      }
      return isValid && isVideo;
    });
    
    if (validFiles.length === 0) {
      setError("No valid video files selected for upload.");
      return;
    }
    
    if (validFiles.length < filesToUpload.length) {
      console.warn(`Filtered out ${filesToUpload.length - validFiles.length} invalid files`);
    }
    
    setUploadingVideos(true);
    setError(null);

    try {
      // Get shopId and userId from selectedShop
      let shopId = selectedShop?._id;
      let userId = selectedShop?.owner?._id || selectedShop?.ownerId || selectedShop?.userId || selectedShop?.createdBy || user?._id;
      
      // If shopId or userId is missing, fetch full shop details first
      if ((!shopId || !userId) && selectedShop?._id) {
        const fullDetails = await fetchShopDetails(selectedShop._id);
        if (fullDetails) {
          setSelectedShop(fullDetails);
          shopId = fullDetails._id;
          userId = fullDetails.owner?._id || fullDetails.ownerId || fullDetails.userId || fullDetails.createdBy;
        }
      }
      
      // Validate we have the required IDs
      if (!shopId) {
        throw new Error('Shop ID is missing. Please select a shop first.');
      }
      if (!userId) {
        throw new Error('User ID is missing. Please ensure the shop has an owner.');
      }
      
      console.log('Uploading videos:', {
        fileCount: validFiles.length,
        shopId,
        userId,
        type: 'product'
      });

      const response = await uploadMultipleFiles(validFiles, shopId, userId, 'product');

      // Handle both singular and plural response formats
      const newVideoUrls = response.data.file_urls || (response.data.file_url ? [response.data.file_url] : []);
      if (newVideoUrls.length > 0) {
        // Use functional update to get the latest state
        setFormData(prev => ({
          ...prev,
          videos: [...(prev.videos || []), ...newVideoUrls]
        }));
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      const msg = err?.response?.data?.message || err.message || "Failed to upload videos";
      setError(msg);
    } finally {
      setUploadingVideos(false);
      // Reset the input
      e.target.value = '';
    }
  };

  // Remove video from the list
  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const updateBulkRow = (rowId, patch) => {
    setBulkRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );
  };

  const handleBulkRowImageUpload = async (rowId, currentImageCount, e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const availableSlots = MAX_PRODUCT_MEDIA_UPLOADS - (currentImageCount || 0);
    if (availableSlots <= 0) {
      setBulkError(`Maximum ${MAX_PRODUCT_MEDIA_UPLOADS} images per product. Remove some images first.`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, availableSlots);
    const validFiles = filesToUpload.filter(
      (file) => file instanceof File || file instanceof Blob
    );
    if (validFiles.length === 0) {
      setBulkError('No valid image files selected.');
      e.target.value = '';
      return;
    }

    setBulkRowUploading({ rowId, kind: 'image' });
    setBulkError(null);

    try {
      let shopId = selectedShop?._id;
      let userId =
        selectedShop?.owner?._id ||
        selectedShop?.ownerId ||
        selectedShop?.userId ||
        selectedShop?.createdBy ||
        user?._id;

      if ((!shopId || !userId) && selectedShop?._id) {
        const fullDetails = await fetchShopDetails(selectedShop._id);
        if (fullDetails) {
          setSelectedShop(fullDetails);
          shopId = fullDetails._id;
          userId =
            fullDetails.owner?._id ||
            fullDetails.ownerId ||
            fullDetails.userId ||
            fullDetails.createdBy;
        }
      }

      if (!shopId) throw new Error('Shop ID is missing.');
      if (!userId) throw new Error('User ID is missing for upload.');

      const response = await uploadMultipleFiles(validFiles, shopId, userId, 'product');
      const newUrls = extractUploadedUrls(response);
      if (newUrls.length === 0) {
        throw new Error(response?.data?.message || 'Upload returned no URLs');
      }

      setBulkRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? { ...r, image: [...(r.image || []), ...newUrls] }
            : r
        )
      );
    } catch (err) {
      console.error('Bulk row image upload error:', err);
      setBulkError(
        err?.response?.data?.message || err.message || 'Failed to upload images'
      );
    } finally {
      setBulkRowUploading(null);
      e.target.value = '';
    }
  };

  const removeBulkRowImage = (rowId, index) => {
    setBulkRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, image: (r.image || []).filter((_, i) => i !== index) }
          : r
      )
    );
  };

  const handleBulkRowVideoUpload = async (rowId, currentVideoCount, e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const availableSlots = MAX_PRODUCT_MEDIA_UPLOADS - (currentVideoCount || 0);
    if (availableSlots <= 0) {
      setBulkError(`Maximum ${MAX_PRODUCT_MEDIA_UPLOADS} videos per product. Remove some videos first.`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, availableSlots);
    const validFiles = filesToUpload.filter(
      (file) =>
        (file instanceof File || file instanceof Blob) &&
        file.type &&
        file.type.startsWith('video/')
    );
    if (validFiles.length === 0) {
      setBulkError('No valid video files selected.');
      e.target.value = '';
      return;
    }

    setBulkRowUploading({ rowId, kind: 'video' });
    setBulkError(null);

    try {
      let shopId = selectedShop?._id;
      let userId =
        selectedShop?.owner?._id ||
        selectedShop?.ownerId ||
        selectedShop?.userId ||
        selectedShop?.createdBy ||
        user?._id;

      if ((!shopId || !userId) && selectedShop?._id) {
        const fullDetails = await fetchShopDetails(selectedShop._id);
        if (fullDetails) {
          setSelectedShop(fullDetails);
          shopId = fullDetails._id;
          userId =
            fullDetails.owner?._id ||
            fullDetails.ownerId ||
            fullDetails.userId ||
            fullDetails.createdBy;
        }
      }

      if (!shopId) throw new Error('Shop ID is missing.');
      if (!userId) throw new Error('User ID is missing for upload.');

      const response = await uploadMultipleFiles(validFiles, shopId, userId, 'product');
      const newUrls = extractUploadedUrls(response);
      if (newUrls.length === 0) {
        throw new Error(response?.data?.message || 'Upload returned no URLs');
      }

      setBulkRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? { ...r, videos: [...(r.videos || []), ...newUrls] }
            : r
        )
      );
    } catch (err) {
      console.error('Bulk row video upload error:', err);
      setBulkError(
        err?.response?.data?.message || err.message || 'Failed to upload videos'
      );
    } finally {
      setBulkRowUploading(null);
      e.target.value = '';
    }
  };

  const removeBulkRowVideo = (rowId, index) => {
    setBulkRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, videos: (r.videos || []).filter((_, i) => i !== index) }
          : r
      )
    );
  };

  const handleBulkSubmit = async () => {
    if (!selectedShop?._id || !canSubmitBulk || bulkSubmitting) return;
    setBulkSubmitting(true);
    setBulkError(null);
    try {
      const products = bulkRows.map((row) => {
        const categoryId = bulkSameCategoryForAll
          ? bulkGlobalCategoryId
          : row.categoryId;
        const item = {
          name: String(row.name).trim(),
          description: String(row.description ?? '').trim(),
          categoryId,
          image: row.image,
          videos: Array.isArray(row.videos) ? row.videos : [],
        };
        if (bulkSamePriceForAll) {
          item.price = Number(bulkGlobalPrice);
        } else {
          item.price = Number(row.price);
        }
        const mrpStr = bulkSameMrpForAll
          ? String(bulkGlobalMrp ?? '').trim()
          : String(row.mrp ?? '').trim();
        if (mrpStr !== '') {
          const mrpNum = Number(mrpStr);
          if (Number.isFinite(mrpNum)) item.mrp = mrpNum;
        }
        const hsnStr = bulkSameHsnForAll
          ? String(bulkGlobalHsn ?? '').trim()
          : String(row.hsnCode ?? '').trim();
        if (hsnStr !== '') item.hsnCode = hsnStr;
        return item;
      });

      const body = {
        shopId: selectedShop._id,
        products,
      };
      if (bulkSamePriceForAll) {
        body.samePriceForAll = true;
        body.globalPrice = Number(bulkGlobalPrice);
      }
      if (bulkSameMrpForAll) {
        body.sameMrpForAll = true;
        const gm = String(bulkGlobalMrp ?? '').trim();
        if (gm !== '') {
          const n = Number(gm);
          if (Number.isFinite(n)) body.globalMrp = n;
        }
      }
      if (bulkSameHsnForAll) {
        body.sameHsnForAll = true;
        const gh = String(bulkGlobalHsn ?? '').trim();
        if (gh !== '') body.globalHsn = gh;
      }
      if (bulkSameCategoryForAll) {
        body.sameCategoryForAll = true;
        body.globalCategoryId = bulkGlobalCategoryId;
      }

      await bulkAddProducts(body);
      closeBulkModal();
      await loadProducts();
    } catch (err) {
      console.error(err);
      setBulkError(
        err?.response?.data?.message || err.message || 'Bulk add failed'
      );
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <div className="shop-management-wrapper" style={{ width: '100%', height: '100vh', isolation: 'isolate' }}>
      <div className="shop-management">
      {/* Header */}
      <div className="shop-management-header">
        <div>
          <h1>Shop Products Management</h1>
          <p>Manage product inventory and approvals for all shops</p>
        </div>
        <div className="header-actions">
          <span className="text-sm text-gray-600">{selectedShop ? `${products.length} products` : `${shops.length} shops`}</span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">!</span>
          {error}
        </div>
      )}

      {/* Compact Filter Toolbar */}
      <div className="filters-toolbar">
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input
            type="text"
            placeholder="Search shops..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input search-input"
          />
        </div>

        <div className="toolbar-divider"></div>

        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="APPROVED">Approved</option>
            <option value="PENDING">Pending</option>
            <option value="REJECTED">Rejected</option>
            <option value="HOLD">Hold</option>
          </select>
        </div>

        {selectedShop && (
          <>
            <div className="toolbar-divider"></div>

            <div className="filter-group">
              <label className="filter-label">Product Search</label>
              <input
                type="text"
                placeholder="Search products..."
                value={productSearch}
                onChange={(e) => {
                  setPage(1);
                  setProductSearch(e.target.value);
                }}
                className="filter-input search-input"
              />
            </div>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={!!approved}
                onChange={(e) => setApproved(e.target.checked ? true : undefined)}
              />
              Approved Only
            </label>

            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              Include Inactive
            </label>

            <div className="filter-group">
              <label className="filter-label">Sort</label>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="filter-select"
              >
                <option value="createdAt">Created Date</option>
                <option value="price">Price</option>
                <option value="ratings.average">Rating</option>
              </select>
            </div>

            <div className="filter-group">
              <select
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                className="filter-select"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Show</label>
              <select
                value={limit}
                onChange={(e) => {
                  setPage(1);
                  setLimit(parseInt(e.target.value || "20", 10));
                }}
                className="filter-select"
              >
                <option value={10}>10 rows</option>
                <option value={20}>20 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>

            <div className="toolbar-divider"></div>

            <button
              type="button"
              onClick={() => {
                if (!isCreating) {
                  resetForm();
                  setIsCreating(true);
                } else {
                  setIsCreating(false);
                  resetForm();
                }
              }}
              className="btn btn-primary"
            >
              {isCreating ? "Cancel" : "New Product"}
            </button>

            <button
              type="button"
              onClick={openBulkModal}
              className="btn btn-secondary"
            >
              Bulk add
            </button>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Sidebar - Shop List */}
        <div className="sidebar">
          <div className="sidebar-header">
            <h3 className="sidebar-title">Shops</h3>
            <p className="sidebar-subtitle">Select shop to manage products</p>
          </div>

          <div className="sidebar-list">
            {loading && !selectedShop ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading shops...</p>
              </div>
            ) : (
              filteredShops.map(shop => (
                <div
                  key={shop._id}
                  className={`sidebar-item ${selectedShop?._id === shop._id ? 'selected' : ''}`}
                  onClick={() => handleShopSelect(shop)}
                >
                  <div className="sidebar-item-header">
                    <img 
                      src={shop.logoUrl || '/KD-icon.png'} 
                      alt={shop.name}
                      className="sidebar-item-logo"
                      onError={(e) => { e.target.src = '/KD-icon.png'; }}
                    />
                    <h4 className="sidebar-item-name">{shop.name}</h4>
                  </div>
                  <p className="sidebar-item-location">{shop.city}, {shop.state}</p>
                  <span className={`sidebar-item-status ${getStatusBadgeClass(shop.status).replace('status-badge ', '')}`}>
                    {shop.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Data Area */}
        <div className="data-area">
          {!selectedShop ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <div className="empty-title">Select a shop to manage products</div>
              <div className="empty-description">
                Choose a shop from the sidebar to view and manage its product inventory
              </div>
            </div>
          ) : (
            <>
              <div className="data-header">
                <div>
                  <h2 className="data-title">Products for {selectedShop.name}</h2>
                  <p className="data-subtitle">{selectedShop.city}, {selectedShop.state} • {products.length} products</p>
                </div>
              </div>

              {/* Product Creation/Edit Form */}
              {isCreating && (
                <div className="form-modal" onClick={() => { resetForm(); setIsCreating(false); }}>
                  <div className="form-container" onClick={(e) => e.stopPropagation()}>
                    <div className="form-header">
                      <h2 className="form-title">{editingId ? 'Edit Product' : 'Create New Product'}</h2>
                      <button className="form-close" onClick={() => { resetForm(); setIsCreating(false); }}>×</button>
                    </div>
                    
                    <div className="form-content">
                      <div className="form-section">
                        <h3 className="form-section-title">Basic Information</h3>
                        <div className="form-grid">
                          <div className="form-group">
                            <label className="form-label">Product Name *</label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => onChangeField("name", e.target.value)}
                              className="form-input"
                              placeholder="Enter product name"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Price * (₹)</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={formData.price}
                              onChange={(e) => onChangeField("price", e.target.value)}
                              className="form-input"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">MRP (₹)</label>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={formData.mrp}
                              onChange={(e) => onChangeField("mrp", e.target.value)}
                              className="form-input"
                              placeholder="Optional"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Category *</label>
                            <p className="text-xs text-gray-500" style={{ margin: '0 0 6px' }}>
                              Search by name or path — only leaf categories can be selected.
                            </p>
                            <CategoryTreeSelect
                              options={categories}
                              value={formData.category}
                              onChange={(v) => onChangeField('category', v)}
                              placeholder={loadingCategories ? 'Loading categories...' : 'Choose subcategory'}
                              disabled={loadingCategories}
                              onlyLeafCategories
                              searchPlaceholder="Search by category name or path (e.g. Clothing › Men)…"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">HSN</label>
                            <input
                              type="text"
                              value={formData.hsnCode}
                              onChange={(e) => onChangeField("hsnCode", e.target.value)}
                              className="form-input"
                              placeholder="e.g. 62044210"
                            />
                          </div>
                          <div className="form-group">
                            <label className="form-label">Tags</label>
                            <input
                              type="text"
                              value={formData.tags}
                              onChange={(e) => onChangeField("tags", e.target.value)}
                              className="form-input"
                              placeholder="tag1, tag2, tag3"
                            />
                          </div>
                          <div className="form-group full-width">
                            <label className="form-label">Description</label>
                            <textarea
                              value={formData.description}
                              onChange={(e) => onChangeField("description", e.target.value)}
                              className="form-textarea"
                              rows={3}
                              placeholder="Enter product description"
                            />
                          </div>
                          <div className="form-group full-width">
                            <label className="form-label">Product Images</label>
                            <div className="image-upload-container">
                              <div className="image-upload-area">
                                <input
                                  type="file"
                                  id="product-images"
                                  multiple
                                  accept="image/*"
                                  onChange={handleImageUpload}
                                  className="image-upload-input"
                                  disabled={uploadingImages}
                                />
                                <label htmlFor="product-images" className="image-upload-label">
                                  {uploadingImages ? (
                                    <div className="upload-loading">
                                      <div className="loading-spinner"></div>
                                      <span>Uploading...</span>
                                    </div>
                                  ) : (
                                    <>
                                      <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor" className="upload-icon">
                                        <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z"/>
                                      </svg>
                                      <span>Click to upload images</span>
                                      <span className="upload-hint">or drag and drop</span>
                                      <span className="upload-limit">Max {MAX_PRODUCT_MEDIA_UPLOADS} images</span>
                                    </>
                                  )}
                                </label>
                              </div>
                              
                              {/* Preview of uploaded images */}
                              {formData.image && formData.image.length > 0 && (
                                <div className="image-preview-grid">
                                  {formData.image.map((imgUrl, index) => (
                                    <div key={index} className="image-preview-item">
                                      <img src={imgUrl} alt={`Preview ${index + 1}`} />
                                      <button
                                        type="button"
                                        className="remove-image-btn"
                                        onClick={() => removeImage(index)}
                                        title="Remove image"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Video Upload Section */}
                          <div className="form-group full-width">
                            <label className="form-label">Product Videos</label>
                            <div className="image-upload-container">
                              <div className="image-upload-area">
                                <input
                                  type="file"
                                  id="product-videos"
                                  multiple
                                  accept="video/*"
                                  onChange={handleVideoUpload}
                                  className="image-upload-input"
                                  disabled={uploadingVideos}
                                />
                                <label htmlFor="product-videos" className="image-upload-label">
                                  {uploadingVideos ? (
                                    <div className="upload-loading">
                                      <div className="loading-spinner"></div>
                                      <span>Uploading...</span>
                                    </div>
                                  ) : (
                                    <>
                                      <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor" className="upload-icon">
                                        <path d="M8 5v14l11-7z"/>
                                      </svg>
                                      <span>Click to upload videos</span>
                                      <span className="upload-hint">or drag and drop</span>
                                      <span className="upload-limit">Max {MAX_PRODUCT_MEDIA_UPLOADS} videos</span>
                                    </>
                                  )}
                                </label>
                              </div>
                              
                              {/* Preview of uploaded videos */}
                              {formData.videos && formData.videos.length > 0 && (
                                <div className="image-preview-grid">
                                  {formData.videos.map((videoUrl, index) => (
                                    <div key={index} className="image-preview-item video-preview-item">
                                      <video src={videoUrl} />
                                      <button
                                        type="button"
                                        className="remove-image-btn"
                                        onClick={() => removeVideo(index)}
                                        title="Remove video"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="form-group">
                            <label className="filter-checkbox">
                              <input
                                type="checkbox"
                                checked={formData.sponsored}
                                onChange={(e) => onChangeField("sponsored", e.target.checked)}
                              />
                              Sponsored Product
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-footer">
                      <button className="btn btn-secondary" onClick={() => { resetForm(); setIsCreating(false); }}>
                        Cancel
                      </button>
                      {editingId ? (
                        <button
                          disabled={!canSubmit || loading || uploadingImages || uploadingVideos}
                          onClick={handleUpdate}
                          className="btn btn-primary"
                        >
                          {loading ? 'Updating...' : 'Update Product'}
                        </button>
                      ) : (
                        <button
                          disabled={!canSubmit || loading || uploadingImages || uploadingVideos}
                          onClick={handleCreate}
                          className="btn btn-success"
                        >
                          {loading ? 'Creating...' : 'Create Product'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {bulkModalOpen && (
                <div
                  className="form-modal"
                  onClick={() => {
                    if (!bulkSubmitting) closeBulkModal();
                  }}
                >
                  <div
                    className="form-container bulk-add-modal"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="form-header">
                      <h2 className="form-title">Bulk add products</h2>
                      <button
                        type="button"
                        className="form-close"
                        aria-label="Close"
                        disabled={bulkSubmitting}
                        onClick={() => {
                          if (!bulkSubmitting) closeBulkModal();
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div className="form-content">
                      {bulkError && (
                        <div className="bulk-add-modal__error error-message">
                          <span className="error-icon">!</span>
                          {bulkError}
                        </div>
                      )}

                      <div className="bulk-add-modal__shared">
                        <h3 className="bulk-add-modal__shared-title">
                          Shared values (optional)
                        </h3>
                        <p className="bulk-add-modal__shared-hint">
                          Fill once to apply to every product. Each field has a
                          &quot;same for all&quot; option below it. Turn a
                          checkbox off to set that field per product in each
                          row.
                        </p>

                        <div className="bulk-add-modal__shared-grid">
                          <div className="bulk-add-modal__shared-field">
                            {bulkSamePriceForAll && (
                              <div className="form-group">
                                <label className="form-label">Price * (₹)</label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className="form-input"
                                  value={bulkGlobalPrice}
                                  onChange={(e) =>
                                    setBulkGlobalPrice(e.target.value)
                                  }
                                  disabled={bulkSubmitting}
                                  placeholder="0.00"
                                />
                              </div>
                            )}
                            <label className="filter-checkbox bulk-add-modal__shared-check">
                              <input
                                type="checkbox"
                                checked={bulkSamePriceForAll}
                                onChange={(e) =>
                                  setBulkSamePriceForAll(e.target.checked)
                                }
                                disabled={bulkSubmitting}
                              />
                              Same price for all products
                            </label>
                          </div>

                          <div className="bulk-add-modal__shared-field">
                            {bulkSameMrpForAll && (
                              <div className="form-group">
                                <label className="form-label">MRP (₹)</label>
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className="form-input"
                                  value={bulkGlobalMrp}
                                  onChange={(e) =>
                                    setBulkGlobalMrp(e.target.value)
                                  }
                                  disabled={bulkSubmitting}
                                  placeholder="Optional"
                                />
                              </div>
                            )}
                            <label className="filter-checkbox bulk-add-modal__shared-check">
                              <input
                                type="checkbox"
                                checked={bulkSameMrpForAll}
                                onChange={(e) =>
                                  setBulkSameMrpForAll(e.target.checked)
                                }
                                disabled={bulkSubmitting}
                              />
                              Same MRP for all products
                            </label>
                          </div>

                          <div className="bulk-add-modal__shared-field">
                            {bulkSameHsnForAll && (
                              <div className="form-group">
                                <label className="form-label">HSN</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={bulkGlobalHsn}
                                  onChange={(e) =>
                                    setBulkGlobalHsn(e.target.value)
                                  }
                                  disabled={bulkSubmitting}
                                  placeholder="e.g. 62044210"
                                />
                              </div>
                            )}
                            <label className="filter-checkbox bulk-add-modal__shared-check">
                              <input
                                type="checkbox"
                                checked={bulkSameHsnForAll}
                                onChange={(e) =>
                                  setBulkSameHsnForAll(e.target.checked)
                                }
                                disabled={bulkSubmitting}
                              />
                              Same HSN for all products
                            </label>
                          </div>

                          <div className="bulk-add-modal__shared-field bulk-add-modal__shared-field--category">
                            {bulkSameCategoryForAll && (
                              <div className="form-group">
                                <label className="form-label">
                                  Category for all rows *
                                </label>
                                <p className="bulk-add-modal__field-hint">
                                  Open the list and type to search — no need to expand the tree.
                                </p>
                                <CategoryTreeSelect
                                  className="bulk-category-picker"
                                  options={categories}
                                  value={bulkGlobalCategoryId}
                                  onChange={(v) => setBulkGlobalCategoryId(v)}
                                  placeholder={
                                    loadingCategories
                                      ? 'Loading categories...'
                                      : 'Tap to search leaf categories'
                                  }
                                  disabled={loadingCategories || bulkSubmitting}
                                  onlyLeafCategories
                                  searchPlaceholder="Search name or full path (e.g. FOOTWEAR › Shoes)…"
                                />
                              </div>
                            )}
                            <label className="filter-checkbox bulk-add-modal__shared-check">
                              <input
                                type="checkbox"
                                checked={bulkSameCategoryForAll}
                                onChange={(e) =>
                                  setBulkSameCategoryForAll(e.target.checked)
                                }
                                disabled={bulkSubmitting}
                              />
                              Same category for all products
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="bulk-add-modal__rows">
                        {bulkRows.map((row, idx) => (
                          <div key={row.id} className="bulk-add-modal__row">
                            <div className="bulk-add-modal__row-header">
                              <span className="bulk-add-modal__row-title">
                                Product {idx + 1}
                              </span>
                              {bulkRows.length > 1 && (
                                <button
                                  type="button"
                                  className="bulk-add-modal__remove-row"
                                  disabled={bulkSubmitting}
                                  onClick={() =>
                                    setBulkRows((prev) => prev.filter((r) => r.id !== row.id))
                                  }
                                >
                                  Remove row
                                </button>
                              )}
                            </div>
                            <div className="form-grid">
                              <div className="form-group">
                                <label className="form-label">Name *</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={row.name}
                                  onChange={(e) =>
                                    updateBulkRow(row.id, { name: e.target.value })
                                  }
                                  disabled={bulkSubmitting}
                                  placeholder="Product name"
                                />
                              </div>
                              <div className="form-group full-width">
                                <label className="form-label">Description</label>
                                <textarea
                                  className="form-textarea"
                                  rows={2}
                                  value={row.description ?? ''}
                                  onChange={(e) =>
                                    updateBulkRow(row.id, { description: e.target.value })
                                  }
                                  disabled={bulkSubmitting}
                                  placeholder="Optional"
                                />
                              </div>
                              {!bulkSameCategoryForAll && (
                                <div className="form-group full-width bulk-add-modal__row-category">
                                  <label className="form-label">Category *</label>
                                  <p className="bulk-add-modal__field-hint">
                                    Search by name or path — only leaf categories can be selected.
                                  </p>
                                  <CategoryTreeSelect
                                    className="bulk-category-picker"
                                    options={categories}
                                    value={row.categoryId}
                                    onChange={(v) =>
                                      updateBulkRow(row.id, { categoryId: v })
                                    }
                                    placeholder={
                                      loadingCategories
                                        ? 'Loading categories...'
                                        : 'Tap to search leaf categories'
                                    }
                                    disabled={loadingCategories || bulkSubmitting}
                                    onlyLeafCategories
                                    searchPlaceholder="Search name or full path…"
                                  />
                                </div>
                              )}
                              {!bulkSameMrpForAll && (
                                <div className="form-group">
                                  <label className="form-label">MRP (₹)</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="form-input"
                                    value={row.mrp ?? ''}
                                    onChange={(e) =>
                                      updateBulkRow(row.id, { mrp: e.target.value })
                                    }
                                    disabled={bulkSubmitting}
                                    placeholder="Optional"
                                  />
                                </div>
                              )}
                              {!bulkSameHsnForAll && (
                                <div className="form-group">
                                  <label className="form-label">HSN</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={row.hsnCode ?? ''}
                                    onChange={(e) =>
                                      updateBulkRow(row.id, { hsnCode: e.target.value })
                                    }
                                    disabled={bulkSubmitting}
                                    placeholder="e.g. 62044210"
                                  />
                                </div>
                              )}
                              {!bulkSamePriceForAll && (
                                <div className="form-group">
                                  <label className="form-label">Price * (₹)</label>
                                  <input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    className="form-input"
                                    value={row.price}
                                    onChange={(e) =>
                                      updateBulkRow(row.id, { price: e.target.value })
                                    }
                                    disabled={bulkSubmitting}
                                    placeholder="0.00"
                                  />
                                </div>
                              )}
                              <div className="form-group full-width">
                                <label className="form-label">Images *</label>
                                <div className="image-upload-container">
                                  <div className="image-upload-area">
                                    <input
                                      type="file"
                                      id={`bulk-images-${row.id}`}
                                      multiple
                                      accept="image/*"
                                      className="image-upload-input"
                                      disabled={
                                        bulkSubmitting ||
                                        bulkRowUploading?.rowId === row.id
                                      }
                                      onChange={(e) =>
                                        handleBulkRowImageUpload(
                                          row.id,
                                          row.image?.length || 0,
                                          e
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`bulk-images-${row.id}`}
                                      className="image-upload-label"
                                    >
                                      {bulkRowUploading?.rowId === row.id &&
                                      bulkRowUploading?.kind === 'image' ? (
                                        <div className="upload-loading">
                                          <div className="loading-spinner" />
                                          <span>Uploading...</span>
                                        </div>
                                      ) : (
                                        <>
                                          <span>Upload images (max {MAX_PRODUCT_MEDIA_UPLOADS})</span>
                                          {/* <span className="upload-hint">
                                            Uses same upload API as single product
                                          </span> */}
                                        </>
                                      )}
                                    </label>
                                  </div>
                                  {row.image?.length > 0 && (
                                    <div className="image-preview-grid">
                                      {row.image.map((url, i) => (
                                        <div key={`${url}-${i}`} className="image-preview-item">
                                          <img src={url} alt="" />
                                          <button
                                            type="button"
                                            className="remove-image-btn"
                                            disabled={bulkSubmitting}
                                            onClick={() => removeBulkRowImage(row.id, i)}
                                            title="Remove"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="form-group full-width">
                                <label className="form-label">Videos</label>
                                <div className="image-upload-container">
                                  <div className="image-upload-area">
                                    <input
                                      type="file"
                                      id={`bulk-videos-${row.id}`}
                                      multiple
                                      accept="video/*"
                                      className="image-upload-input"
                                      disabled={
                                        bulkSubmitting ||
                                        bulkRowUploading?.rowId === row.id
                                      }
                                      onChange={(e) =>
                                        handleBulkRowVideoUpload(
                                          row.id,
                                          row.videos?.length || 0,
                                          e
                                        )
                                      }
                                    />
                                    <label
                                      htmlFor={`bulk-videos-${row.id}`}
                                      className="image-upload-label"
                                    >
                                      {bulkRowUploading?.rowId === row.id &&
                                      bulkRowUploading?.kind === 'video' ? (
                                        <div className="upload-loading">
                                          <div className="loading-spinner" />
                                          <span>Uploading...</span>
                                        </div>
                                      ) : (
                                        <>
                                          <span>Upload videos (max {MAX_PRODUCT_MEDIA_UPLOADS})</span>
                                        </>
                                      )}
                                    </label>
                                  </div>
                                  {row.videos?.length > 0 && (
                                    <div className="image-preview-grid">
                                      {row.videos.map((url, i) => (
                                        <div
                                          key={`${url}-${i}`}
                                          className="image-preview-item video-preview-item"
                                        >
                                          <video src={url} />
                                          <button
                                            type="button"
                                            className="remove-image-btn"
                                            disabled={bulkSubmitting}
                                            onClick={() => removeBulkRowVideo(row.id, i)}
                                            title="Remove"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="bulk-add-modal__add-row-wrap">
                        <button
                          type="button"
                          className="bulk-add-modal__add-row"
                          disabled={bulkSubmitting}
                          onClick={() =>
                            setBulkRows((prev) => {
                              const row = newBulkRow();
                              if (bulkSameCategoryForAll && bulkGlobalCategoryId) {
                                row.categoryId = bulkGlobalCategoryId;
                              }
                              return [...prev, row];
                            })
                          }
                        >
                          Add another product
                        </button>
                      </div>
                    </div>

                    <div className="form-footer">
                      <button
                        type="button"
                        className="bulk-add-modal__action-cancel"
                        disabled={bulkSubmitting}
                        onClick={() => {
                          if (!bulkSubmitting) closeBulkModal();
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="bulk-add-modal__action-submit"
                        disabled={
                          !canSubmitBulk ||
                          bulkSubmitting ||
                          bulkRowUploading != null
                        }
                        onClick={handleBulkSubmit}
                      >
                        {bulkSubmitting ? 'Submitting...' : 'Submit bulk add'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Products Table */}
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Video</th>
                      <th>Product</th>
                      <th>Price</th>
                      <th>MRP</th>
                      <th>Category</th>
                      <th>HSN</th>
                      <th>Approved</th>
                      <th>Active</th>
                      <th>Rating</th>
                      <th>Tags</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={13}>
                          <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Loading products...</p>
                          </div>
                        </td>
                      </tr>
                    ) : products.length === 0 ? (
                      <tr>
                        <td colSpan={13}>
                          <div className="empty-state">
                            <div className="empty-icon">📦</div>
                            <div className="empty-title">No products found</div>
                            <div className="empty-description">
                              This shop doesn't have any products yet. Create one to get started!
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      products.map((p) => (
                        <tr key={p._id}>
                          <td>
                            <div className="product-media-cell">
                              {p.image && p.image.length > 0 ? (
                                <div className="product-image-carousel">
                                  <img
                                    src={p.image[currentImageIndices[p._id] || 0]}
                                    alt={p.name}
                                    className="product-table-image clickable"
                                    onClick={() => handleImageClick(p.image[currentImageIndices[p._id] || 0], p.image, currentImageIndices[p._id] || 0)}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  {p.image.length > 1 && (
                                    <>
                                      <button
                                        className="carousel-nav-btn carousel-nav-left"
                                        onClick={(e) => { e.stopPropagation(); navigateTableImage(p._id, p.image, -1); }}
                                        disabled={(currentImageIndices[p._id] || 0) === 0}
                                      >
                                        ‹
                                      </button>
                                      <button
                                        className="carousel-nav-btn carousel-nav-right"
                                        onClick={(e) => { e.stopPropagation(); navigateTableImage(p._id, p.image, 1); }}
                                        disabled={(currentImageIndices[p._id] || 0) === p.image.length - 1}
                                      >
                                        ›
                                      </button>
                                      <div className="carousel-indicator">
                                        {(currentImageIndices[p._id] || 0) + 1}/{p.image.length}
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="product-table-image-placeholder">
                                  <span>No Image</span>
                                </div>
                              )}
                              {p.videos && p.videos.length > 0 && (
                                <div className="product-video-indicator" title={`${p.videos.length} video(s)`}>
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            {p.videos && p.videos.length > 0 ? (
                              <div className="product-videos-cell">
                                <div className="product-video-carousel">
                                  <div 
                                    className="product-video-item"
                                    onClick={() => handleVideoClick(p.videos[currentVideoIndices[p._id] || 0], p.videos, currentVideoIndices[p._id] || 0)}
                                  >
                                    <video
                                      src={p.videos[currentVideoIndices[p._id] || 0]}
                                      className="product-table-video"
                                      preload="metadata"
                                      onMouseOver={(e) => e.target.play()}
                                      onMouseOut={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <div className="product-video-play-overlay">
                                      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                        <path d="M8 5v14l11-7z"/>
                                      </svg>
                                    </div>
                                  </div>
                                  {p.videos.length > 1 && (
                                    <>
                                      <button
                                        className="carousel-nav-btn carousel-nav-left video-nav-left"
                                        onClick={(e) => { e.stopPropagation(); navigateTableVideo(p._id, p.videos, -1); }}
                                        disabled={(currentVideoIndices[p._id] || 0) === 0}
                                      >
                                        ‹
                                      </button>
                                      <button
                                        className="carousel-nav-btn carousel-nav-right video-nav-right"
                                        onClick={(e) => { e.stopPropagation(); navigateTableVideo(p._id, p.videos, 1); }}
                                        disabled={(currentVideoIndices[p._id] || 0) === p.videos.length - 1}
                                      >
                                        ›
                                      </button>
                                      <div className="carousel-indicator video-carousel-indicator">
                                        {(currentVideoIndices[p._id] || 0) + 1}/{p.videos.length}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="product-table-video-placeholder">
                                <span>No Video</span>
                              </div>
                            )}
                          </td>
                          <td className="font-semibold" title={p.name}>
                            {p.name.length > 30 ? `${p.name.substring(0, 30)}...` : p.name}
                          </td>
                          <td>₹{p.price}</td>
                          <td>
                            {p.mrp != null && p.mrp !== "" ? `₹${p.mrp}` : "—"}
                          </td>
                          <td title={p.category?.name || p.category}>
                            {p.category?.name || String(p.category).slice(0, 10)}
                          </td>
                          <td title={String(p.hsnCode ?? p.hsn ?? "")}>
                            {p.hsnCode != null && p.hsnCode !== ""
                              ? String(p.hsnCode)
                              : p.hsn != null && p.hsn !== ""
                                ? String(p.hsn)
                                : "—"}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                p.isApproved ? "status-approved" : "status-pending"
                              }`}
                            >
                              {p.isApproved ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                p.isActive ? "status-approved" : "status-rejected"
                              }`}
                            >
                              {p.isActive ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            ⭐ {p.ratings?.average?.toFixed(1) ?? 0} ({p.ratings?.totalReviews ?? 0})
                          </td>
                          <td title={(p.tags || []).join(", ")}>
                            {(p.tags || []).slice(0, 2).join(", ")}
                            {(p.tags || []).length > 2 && "..."}
                          </td>
                          <td>
                            {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "-"}
                          </td>
                          <td>
                            <div className="table-actions">
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => populateFormForEdit(p)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(p._id)}
                              >
                                Delete
                              </button>
                              <button
                                className={`btn btn-sm ${p.isApproved ? 'btn-secondary' : 'btn-success'}`}
                                onClick={() => toggleApproval(p)}
                              >
                                {p.isApproved ? "Unapprove" : "Approve"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="pagination">
                <div className="pagination-info">
                  Page {page} of {totalPages}
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Video Modal */}
        {videoModalOpen && selectedVideo && (
          <div className="video-modal-overlay" onClick={() => setVideoModalOpen(false)}>
            <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="video-modal-close" onClick={() => setVideoModalOpen(false)}>
                ×
              </button>
              <video
                src={selectedVideo}
                className="video-modal-player"
                controls
                autoPlay
              />
              {currentProductVideos.length >= 1 && (
                <div className="video-modal-nav">
                  <button
                    className="video-modal-nav-btn"
                    onClick={() => navigateVideo(-1)}
                    disabled={selectedVideoIndex === 0}
                  >
                    ‹
                  </button>
                  <span className="video-modal-counter">
                    {selectedVideoIndex + 1} / {currentProductVideos.length}
                  </span>
                  <button
                    className="video-modal-nav-btn"
                    onClick={() => navigateVideo(1)}
                    disabled={selectedVideoIndex === currentProductVideos.length - 1}
                  >
                    ›
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Image Modal */}
        {imageModalOpen && selectedImage && (
          <div className="image-modal-overlay" onClick={() => setImageModalOpen(false)}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="image-modal-close" onClick={() => setImageModalOpen(false)}>
                ×
              </button>
              <img
                src={selectedImage}
                alt="Product"
                className="image-modal-image"
              />
              {/* Always show navigation arrows when modal is open */}
              <div className="image-modal-nav">
                <button
                  className="image-modal-nav-btn"
                  onClick={() => navigateImage(-1)}
                  disabled={selectedImageIndex === 0 || currentProductImages.length <= 1}
                >
                  ‹
                </button>
                <span className="image-modal-counter">
                  {selectedImageIndex + 1} / {currentProductImages.length}
                </span>
                <button
                  className="image-modal-nav-btn"
                  onClick={() => navigateImage(1)}
                  disabled={selectedImageIndex === currentProductImages.length - 1 || currentProductImages.length <= 1}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

export default ShopProducts;